type AsyncTask<T> = () => Promise<T>;
type OnCompleteCallback<T> = (result: T, error: Error | null) => Promise<void>;

interface QueueItem<T> {
  task: AsyncTask<T>;
  resolve: (value: T) => void;
  reject: (error: Error) => void;
}

export const DEFAULT_CONCURRENCY_LIMIT = 5;

export class ConcurrentQueue<T = unknown> {
  private queue: QueueItem<T>[] = [];

  private activeCount = 0;

  private readonly concurrencyLimit: number;

  private readonly onComplete?: OnCompleteCallback<T>;

  constructor(options: { concurrencyLimit: number; onComplete?: OnCompleteCallback<T> }) {
    this.concurrencyLimit = options.concurrencyLimit;
    this.onComplete = options.onComplete;
  }

  push(task: AsyncTask<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push({ task, resolve, reject });
      this.processNext().catch(() => {});
    });
  }

  private async processNext(): Promise<void> {
    if (this.activeCount >= this.concurrencyLimit || this.queue.length === 0) {
      return;
    }

    const item = this.queue.shift();
    if (!item) return;

    this.activeCount += 1;

    try {
      const result = await item.task();

      if (this.onComplete) {
        await this.onComplete(result, null);
      }

      item.resolve(result);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));

      if (this.onComplete) {
        await this.onComplete(undefined as T, err);
      }

      item.reject(err);
    } finally {
      this.activeCount -= 1;
      this.processNext().catch(() => {});
    }
  }

  get pending(): number {
    return this.queue.length;
  }

  get active(): number {
    return this.activeCount;
  }

  clear(): void {
    this.queue = [];
  }
}
